import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import TextField from "../../components/customMui/TextField";
import LoadingButton from "../../components/customMui/LoadingButton";
import Button from "../../components/customMui/Button";
import Alert from "../../components/customMui/Alert";
import standardBizApiClient from "../../util/BizApi/standardBizApiClient";
import Check from '@mui/icons-material/Done';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { setCertList, setCertCodeSubscribedList, setCertCodeInFocus } from "../../redux/slices/practiceSlice";
import { setIsExamInProgress, setLatestExamType } from "../../redux/slices/examSlice";
import { login, primaryRole } from "../../redux/slices/userSlice";
import "./signupForm.css";
import Footer from "../../components/footer/Footer";
import pagePath from "../../util/constants/pagePath";
import { websiteBaseUrl } from "../../util/urlConfig";

const SignupForm = ({ selectedPlan, onPlanRequired, emailRef }) => {
    const [submitLoading, setSubmitLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [emailAddress, setEmailAddress] = useState("");
    const [uppercaseValidation, setUppercaseValidation] = useState(false);
    const [lowercaseValidation, setLowercaseValidation] = useState(false);
    const [digitValidation, setDigitValidation] = useState(false);
    const [specialCharValidation, setSpecialCharValidation] = useState(false);
    const [lengthValidation, setLengthValidation] = useState(false);
    const [passwordValidated, setPasswordValidated] = useState(false);
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showSuccess, setShowSuccess] = useState(false);
    const [showConfirmSuccess, setShowConfirmSuccess] = useState(false);
    const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
    const [otpValues, setOtpValues] = useState(["", "", "", "", "", ""]);
    const inputRefs = useRef([]);
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState(null);
    const [resendLoading, setResendLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(300);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const isPlanSelected = !!selectedPlan;
    const passwordMismatchError = errorMessage === "Passwords do not match";
    const passwordRequirements = [
        { label: "8 char long", valid: lengthValidation },
        { label: "1 upp case", valid: uppercaseValidation },
        { label: "1 low case", valid: lowercaseValidation },
        { label: "1 num", valid: digitValidation },
        { label: "1 sp char", valid: specialCharValidation },
    ];

    useEffect(() => {
        if (showSuccess && !showConfirmSuccess && timeLeft > 0) {
            const timerId = setInterval(() => {
                setTimeLeft(prev => prev - 1);
            }, 1000);
            return () => clearInterval(timerId);
        }
    }, [showSuccess, showConfirmSuccess, timeLeft]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const handleOtpChange = (e, index) => {
        const value = e.target.value;
        if (/[^0-9]/.test(value)) return;

        const digit = value.slice(-1);
        const newOtp = [...otpValues];
        newOtp[index] = digit;
        setOtpValues(newOtp);

        if (digit && index < 5) {
            inputRefs.current[index + 1].focus();
        }
    };

    const handleOtpKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (!otpValues[index] && index > 0) {
                inputRefs.current[index - 1].focus();
            }
        }
    };

    const handleOtpPaste = (e) => {
        e.preventDefault();
        const pasteData = e.clipboardData.getData("text").replace(/[^0-9]/g, "").slice(0, 6).split("");
        if (pasteData.length > 0) {
            const newOtp = [...otpValues];
            pasteData.forEach((char, i) => {
                if (i < 6) newOtp[i] = char;
            });
            setOtpValues(newOtp);
            const nextIndex = Math.min(pasteData.length, 5);
            inputRefs.current[nextIndex].focus();
        }
    };

    const handleOtpConfirm = async e => {
        e.preventDefault();
        const otpCode = otpValues.join("");
        if (otpCode.length !== 6) {
            setOtpError("Please enter all 6 digits.");
            return;
        }
        setOtpLoading(true);
        setOtpError(null);

        const confirmRes = await standardBizApiClient.account_signup_confirm(emailAddress, otpCode);
        if (confirmRes?.is_error) {
            setOtpError("Error verifying OTP. Please check the code and try again.");
            setOtpLoading(false);
            return;
        }

        try {
            const encodedEmailAddress = emailAddress.replace(/@/g, "%40").replace(/\./g, "..");
            const loginRes = await standardBizApiClient.account_login(
                encodedEmailAddress,
                password,
                ""
            );

            if (loginRes?.is_error) {
                setShowConfirmSuccess(true);
                setOtpLoading(false);
                return;
            }

            const { user_id, certs, primary_role, exam_meta, email_address } = loginRes;
            const { cert_code_in_focus, cert_code_subscribed_list, cert_list } = certs;
            const { exam_type } = exam_meta;
            const newIsExamInProgress = Boolean(exam_type);

            dispatch(setCertCodeInFocus(cert_code_in_focus));
            dispatch(setCertCodeSubscribedList(cert_code_subscribed_list));
            dispatch(setCertList(cert_list));

            dispatch(setLatestExamType(exam_type));
            dispatch(setIsExamInProgress(newIsExamInProgress));
            dispatch(login({ userId: user_id, email: email_address }));
            dispatch(primaryRole(primary_role));

            const selectedCert = loginRes?.selected_cert_code || selectedPlan || localStorage.getItem("selected_cert_code");

            if (!cert_code_subscribed_list || cert_code_subscribed_list.length === 0) {
                if (selectedCert) {
                    navigate(`${pagePath.practice_no_subscribed_certs}?cert=${selectedCert}`);
                } else {
                    navigate(pagePath.practice_no_subscribed_certs);
                }
            } else {
                navigate(pagePath.dashboard);
            }
            localStorage.removeItem("selected_cert_code");
        } catch (error) {
            setShowConfirmSuccess(true);
        }

        setOtpLoading(false);
    };

    const handleResendOtp = async () => {
        setResendLoading(true);
        setOtpError(null);
        setOtpValues(["", "", "", "", "", ""]);
        const resendRes = await standardBizApiClient.account_signup_resend_otp(emailAddress);
        if (resendRes?.is_error) {
            setOtpError("Failed to resend OTP. Please try again.");
        } else {
            setTimeLeft(300); // 10 minutes reset
        }
        setResendLoading(false);
    };

    const handlePasswordValidation = (value) => {
        let oneUppercaseValidation = /^(?=.*[A-Z])/.test(value)
        let oneLowercaseValidation = /^(?=.*[a-z])/.test(value)
        let oneDigitValidation = /^(?=.*[0-9])/.test(value)
        let oneSpecialCharValidation = /^(?=.*[^a-zA-Z0-9\s])/.test(value)
        let minLengthValidation = value.length > 7

        setUppercaseValidation(oneUppercaseValidation)
        setLowercaseValidation(oneLowercaseValidation)
        setDigitValidation(oneDigitValidation)
        setSpecialCharValidation(oneSpecialCharValidation)
        setLengthValidation(minLengthValidation)
        if (oneUppercaseValidation && oneLowercaseValidation && oneDigitValidation && oneSpecialCharValidation && minLengthValidation) {
            setPasswordValidated(true)
        } else {
            setPasswordValidated(false)
        }
        setPassword(value)
    }

    const handleFieldFocus = () => {
        if (!isPlanSelected) {
            onPlanRequired && onPlanRequired();
        }
    }

    const handlePasswordFocus = () => {
        handleFieldFocus();
        setShowPasswordRequirements(true);
    }

    const handleSignup = async e => {
        e.preventDefault();

        if (!isPlanSelected) {
            onPlanRequired && onPlanRequired();
            return;
        }

        setErrorMessage(null)
        if (password !== confirmPassword) {
            setErrorMessage("Passwords do not match")
            return;
        }
        setSubmitLoading(true);
        const signupRes = await standardBizApiClient.account_signup(emailAddress, password, selectedPlan);
        if (signupRes?.is_error) {
            setErrorMessage("Error while signing up, please try again soon.");
            setSubmitLoading(false);
            return;
        }
        setShowSuccess(true);
        setSubmitLoading(false);
    }


    if (showConfirmSuccess) {
        const selectedCertCodeStr = selectedPlan ? `?cert=${selectedPlan}` : "";
        return (
            <>
                <div className="signup signup-success">
                    <h2 className="font-header-light">Welcome to WannaPractice!</h2>
                    <p>Your account is ready to use.</p>
                    <div style={{ marginTop: '20px' }}>
                        <Link to={`${pagePath.login}${selectedCertCodeStr}`}>
                            <Button>Continue to Login</Button>
                        </Link>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (showSuccess) {
        return (
            <>
                <div className="signup-form-shell" style={{ marginTop: '30px' }}>
                    <form className="signup-form" onSubmit={handleOtpConfirm}>
                        <h2 className="font-header-light" style={{ textAlign: "center" }}>Enter Verification Code</h2>
                        <p style={{ textAlign: "center", marginBottom: '20px' }}>
                            WannaPractice Support (support@wannapractice.com) sent an email with a code to verify <strong>{emailAddress}</strong>.
                        </p>
                        {otpError ? (<Alert severity="error">{otpError}</Alert>) : null}

                        <div className="otp-container">
                            {otpValues.map((digit, idx) => (
                                <input
                                    key={idx}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={2}
                                    className="otp-box"
                                    value={digit}
                                    disabled={otpLoading}
                                    ref={(el) => (inputRefs.current[idx] = el)}
                                    onChange={(e) => handleOtpChange(e, idx)}
                                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                                    onPaste={handleOtpPaste}
                                />
                            ))}
                        </div>

                        <LoadingButton customProps={{ type: "submit", loading: otpLoading, disabled: otpValues.join("").length !== 6 }}>Confirm OTP</LoadingButton>

                        <div style={{ textAlign: "center", marginTop: "15px" }}>
                            {timeLeft > 0 ? (
                                <p>You can resend the code in {formatTime(timeLeft)}</p>
                            ) : (
                                <LoadingButton
                                    customProps={{
                                        type: "button",
                                        onClick: handleResendOtp,
                                        loading: resendLoading
                                    }}
                                >
                                    Resend Code
                                </LoadingButton>
                            )}
                        </div>
                    </form>
                </div>
                <Footer />
            </>
        )
    }

    return (
        <>
            <div className="signup-form-shell">
                <form className="signup-form" onSubmit={handleSignup} >
                    <div className="signupFormIntro">
                        <p className="signupFormKicker">CREATE YOUR ACCOUNT</p>
                        <h3>Set up your access in one quick step</h3>
                        <p>
                            Enter your details below, confirm your email, and start practicing for the certification you selected.
                        </p>
                    </div>
                    {errorMessage && !passwordMismatchError ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                    <TextField
                        required
                        disabled={submitLoading}
                        value={emailAddress}
                        onChange={e => setEmailAddress(e.target.value)}
                        onFocus={handleFieldFocus}
                        id="signup-email-address"
                        type="email"
                        label="Email Address"
                        autoComplete="new-password"
                        inputRef={emailRef}
                    />
                    <TextField
                        required
                        disabled={submitLoading}
                        value={password}
                        onChange={e => handlePasswordValidation(e.target.value)}
                        onFocus={handlePasswordFocus}
                        id="signup-password"
                        type="password"
                        label="Password"
                        autoComplete="new-password"
                    />
                    {showPasswordRequirements ? (
                        <div className="password-requirements">
                            <ul className="password-requirements-list">
                                {passwordRequirements.map(({ label, valid }) => (
                                    <li key={label} className={`password-req-item pass-validation-${valid}`}>
                                        {valid ? <Check fontSize="small" /> : <HighlightOffIcon fontSize="small" />}
                                        <span>{label}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}
                    <TextField
                        required
                        disabled={submitLoading || !passwordValidated}
                        value={confirmPassword}
                        onChange={e => setConfirmPassword(e.target.value)}
                        onFocus={handleFieldFocus}
                        id="signup-confirm-password"
                        type="password"
                        label="Retype Password"
                        autoComplete="new-password"
                    />
                    {passwordMismatchError ? (<Alert severity="error">{errorMessage}</Alert>) : null}
                    <p className="terms-and-policy">By clicking on "Sign Up", you agree to WannaPractice's <a href={`${websiteBaseUrl}/terms-of-use/`}>Terms Of Use</a>, including our <a href={`${websiteBaseUrl}/privacy-policy/`}>Privacy Policy</a></p>
                    <div className="signupBtnWrap">
                        <div className={!isPlanSelected ? "signupButtonDisabled" : ""}>
                            <LoadingButton customProps={{ type: "submit", loading: submitLoading, disabled: !isPlanSelected }}>Sign Up</LoadingButton>
                        </div>
                        {!isPlanSelected ? (
                            <button
                                type="button"
                                className="signupBtnOverlay"
                                aria-label="Choose plan before signup"
                                onClick={() => onPlanRequired && onPlanRequired()}
                            />
                        ) : null}
                    </div>
                </form>
            </div>
            <Footer />
        </>
    )
}

export default SignupForm;
